import { useState } from "react";
import { Container, CircularProgress, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Navigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { useQueryClient } from "@tanstack/react-query";

import { useUsersSettingService } from "@/services/useUsersSettingService";
import LoadAvatar from "@/pages/user/profile/loadAvatar";
import CertificateLoader from "./certificateLoader";
import useGetMyCertificates from "@/hooks/useGetMyCertificates";
import ChangePassword from "./changePassword";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import axios from "@/api/axios";
import { useSession } from "@/hooks/useSession";
import { useTranslation } from "react-i18next";

// Import sub-components
import ProfileHeader from "./components/ProfileHeader";
import ProfileOverviewCard from "./components/ProfileOverviewCard";
import PersonalInfoTab from "./components/PersonalInfoTab";
import WorkplaceTab from "./components/WorkplaceTab";
import CertificatesTab from "./components/CertificatesTab";
import SecurityTab from "./components/SecurityTab";
import EmailVerificationDialog from "./components/EmailVerificationDialog";

const UserProfile = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { token, session } = useSession();

  const [openAvatarModal, setOpenAvatarModal] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [certificateLoader, setCertificateLoader] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [verifyEmailDialogOpen, setVerifyEmailDialogOpen] = useState(false);

  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  // Fetch user details and certificates only if session is valid
  const userId = session?.id;

  const { userDetails, isUserDetailsLoading } = useUsersSettingService(
    session?.id
  );

  const avatarKey =
    userDetails?.avatar?.updated_at ||
    userDetails?.updated_at ||
    userDetails?.avatar?.id ||
    "no-avatar";

  const { MyCertificates, refetchMyCertificates } = useGetMyCertificates(
    userId,
    { enabled: !!session?.id }
  );

  const handleOpenAvatarModal = () => setOpenAvatarModal(true);
  const handleCloseAvatarModal = () => setOpenAvatarModal(false);
  const handleLoadCertificate = () => setCertificateLoader(true);
  const handleLoadCertificateClose = () => setCertificateLoader(false);

  // Function to send verification request using axios with POST method
  const handleVerifyEmail = async () => {
    if (!token) return;
    try {
      const response = await axios.post(
        "/api/v1/users/verify-token",
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 201) {
        setVerifyEmailDialogOpen(true);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // Redirect to login if no valid session
  if (!session?.id) {
    return <Navigate to="/sign-in" replace />;
  }

  // Handle session loading
  if (session === undefined) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: theme.palette.background.default,
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} />
        <Typography sx={{ ml: 2 }} color="text.secondary">
          {t("userProfile.loadingSessionText")}
        </Typography>
      </Box>
    );
  }

  if (isUserDetailsLoading) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: theme.palette.background.default,
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress
          size={40}
          aria-label={t("userProfile.loadingProfileAria")}
        />
        <Typography sx={{ ml: 2 }} color="text.secondary">
          Loading profile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Page Header with Breadcrumbs */}
      <ProfileHeader />

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {/* Profile Overview Card */}
          <Grid size={12}>
            <ProfileOverviewCard
              userDetails={userDetails}
              avatarKey={avatarKey}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </Grid>

          {/* Tab Content */}
          <Grid size={12}>
            <Box sx={{ py: { xs: 3, md: 4 } }}>
              {/* Personal Information Tab */}
              {activeTab === 0 && (
                <PersonalInfoTab
                  userDetails={userDetails}
                  token={token}
                  onVerifyEmail={handleVerifyEmail}
                />
              )}

              {/* Workplace Tab */}
              {activeTab === 1 && <WorkplaceTab userDetails={userDetails} />}

              {/* Certificates Tab */}
              {activeTab === 2 && (
                <CertificatesTab
                  userDetails={userDetails}
                  certificates={MyCertificates}
                  sessionId={session?.id}
                  onLoadCertificate={handleLoadCertificate}
                  handleApiError={handleApiError}
                  setToast={setToast}
                  onCertificateDelete={refetchMyCertificates}
                />
              )}

              {/* Security Tab */}
              {activeTab === 3 && (
                <SecurityTab
                  userDetails={userDetails}
                  onChangePassword={() => setOpenPassword(true)}
                  onChangeAvatar={handleOpenAvatarModal}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Modals: Render only if session is valid */}
      {session?.id && certificateLoader && (
        <CertificateLoader
          open={certificateLoader}
          onClose={handleLoadCertificateClose}
          userId={session.id}
          setToast={setToast}
          handleApiError={handleApiError}
          onCertificateLoad={refetchMyCertificates}
        />
      )}

      {session?.id && openAvatarModal && (
        <LoadAvatar
          open={openAvatarModal}
          onClose={handleCloseAvatarModal}
          user={session}
          avatarUrl={userDetails?.avatar?.url}
          setToast={setToast}
          handleApiError={handleApiError}
          onAvatarUpload={async (updatedUser) => {
            if (updatedUser) {
              // Instant UI update
              queryClient.setQueryData(["userDetails", userId], updatedUser);
            }
            // Close the dialog so the change is visible right away
            setOpenAvatarModal(false);
            // Optional: background verify with server
            queryClient.invalidateQueries({
              queryKey: ["userDetails", userId],
            });
          }}
        />
      )}

      {session?.id && openPassword && (
        <ChangePassword
          open={openPassword}
          onClose={() => setOpenPassword(false)}
          setToast={setToast}
          toast={toast}
        />
      )}

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />

      {/* Email Verification Dialog */}
      <EmailVerificationDialog
        open={verifyEmailDialogOpen}
        onClose={() => setVerifyEmailDialogOpen(false)}
        userEmail={userDetails?.email}
      />
    </Box>
  );
};

export default UserProfile;
